import React, { createContext, useContext, useEffect, useState } from 'react';
import { isMobileDevice, getDeviceType } from '../utils/deviceUtils';

interface MobileVersionContextType {
  isMobileView: boolean;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  isFullVersion: boolean;
  setIsFullVersion: (value: boolean) => void;
  toggleMobileView: () => void;
  // Emergency desktop mode for mobile users
  isEmergencyDesktopMode: boolean;
  enableEmergencyDesktopMode: () => void;
  disableEmergencyDesktopMode: () => void;
  toggleEmergencyDesktopMode: () => void;
}

const MobileVersionContext = createContext<MobileVersionContextType | undefined>(undefined);

export const MobileVersionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [isFullVersion, setIsFullVersion] = useState(true);
  const [isEmergencyDesktopMode, setIsEmergencyDesktopMode] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const mobile = isMobileDevice();
      const type = getDeviceType();
      
      // Determine device type for popup system
      let popupDeviceType: 'mobile' | 'tablet' | 'desktop';
      if (type === 'ios' || type === 'android') {
        popupDeviceType = 'mobile';
      } else if (mobile && window.innerWidth > 768) {
        popupDeviceType = 'tablet';
      } else if (mobile) {
        popupDeviceType = 'mobile';
      } else {
        popupDeviceType = 'desktop';
      }
      
      setDeviceType(popupDeviceType);
      setIsMobileView(mobile);
      
      // Set default view based on device and user preference
      if (mobile && !localStorage.getItem('preferFullVersion')) {
        setIsFullVersion(false);
      } else if (mobile && localStorage.getItem('preferFullVersion') === 'true') {
        setIsFullVersion(true);
      } else if (!mobile) {
        setIsFullVersion(true);
      }

      // Load emergency desktop mode preference
      const emergencyMode = localStorage.getItem('emergencyDesktopMode') === 'true';
      setIsEmergencyDesktopMode(emergencyMode);
    };

    checkDevice();
    
    // Check on window resize
    const handleResize = () => {
      checkDevice();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleMobileView = () => {
    const newValue = !isFullVersion;
    setIsFullVersion(newValue);
    localStorage.setItem('preferFullVersion', newValue.toString());
  };

  // Emergency desktop mode functions
  const enableEmergencyDesktopMode = () => {
    setIsEmergencyDesktopMode(true);
    localStorage.setItem('emergencyDesktopMode', 'true');
  };

  const disableEmergencyDesktopMode = () => {
    setIsEmergencyDesktopMode(false);
    localStorage.setItem('emergencyDesktopMode', 'false');
  };

  const toggleEmergencyDesktopMode = () => {
    const newValue = !isEmergencyDesktopMode;
    setIsEmergencyDesktopMode(newValue);
    localStorage.setItem('emergencyDesktopMode', newValue.toString());
  };

  return (
    <MobileVersionContext.Provider value={{
      isMobileView,
      deviceType,
      isFullVersion,
      setIsFullVersion,
      toggleMobileView,
      // Emergency desktop mode
      isEmergencyDesktopMode,
      enableEmergencyDesktopMode,
      disableEmergencyDesktopMode,
      toggleEmergencyDesktopMode,
    }}>
      {children}
    </MobileVersionContext.Provider>
  );
};

export const useMobileVersion = () => {
  const context = useContext(MobileVersionContext);
  if (!context) {
    throw new Error('useMobileVersion must be used within a MobileVersionProvider');
  }
  return context;
};
